import React, { useState, useEffect, useCallback } from 'react';
import AppOverview from '../components/tour/AppOverview';
import UserGuide from '../components/support/UserGuide';
import { useAuth } from '../contexts/AuthContext';
import { fetchWithActivity } from '../utils/fetchWithActivity';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

interface SupportTicket {
  id: string;
  ticketNumber: string;
  subject: string;
  description: string;
  category: string;
  priority: string;
  status: string;
  assignedTo: string | null;
  resolvedAt: string | null;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
  user?: { id: string; firstName: string; lastName: string; email: string };
  assignee?: { id: string; firstName: string; lastName: string } | null;
  company?: { id: string; name: string } | null;
  replies?: SupportTicketReply[];
}

interface SupportTicketReply {
  id: string;
  message: string;
  isStaffReply: boolean;
  createdAt: string;
  user: { id: string; firstName: string; lastName: string; role: string };
}

type View = 'list' | 'create' | 'detail';

const CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'bug', label: 'Bug Report' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'billing', label: 'Billing' },
  { value: 'account', label: 'Account' },
  { value: 'technical', label: 'Technical' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const STATUS_STYLES: Record<string, string> = {
  open: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  in_progress: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  waiting_on_customer: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  waiting_on_support: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  resolved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  closed: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
};

const STATUS_LABELS: Record<string, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  waiting_on_customer: 'Waiting on You',
  waiting_on_support: 'Waiting on Support',
  resolved: 'Resolved',
  closed: 'Closed',
};

const PRIORITY_STYLES: Record<string, string> = {
  low: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  medium: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  high: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  urgent: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};

const Support: React.FC = () => {
  const { token } = useAuth();
  const [view, setView] = useState<View>('list');
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  // Create form state
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('general');
  const [priority, setPriority] = useState('medium');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Reply state
  const [replyMessage, setReplyMessage] = useState('');
  const [replySubmitting, setReplySubmitting] = useState(false);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (statusFilter !== 'all') params.append('status', statusFilter);

      const res = await fetchWithActivity(`${API_BASE_URL}/api/support-tickets?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setTickets(data.tickets);
        setTotal(data.total);
      }
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
    } finally {
      setLoading(false);
    }
  }, [token, page, statusFilter]);

  const fetchTicketDetail = useCallback(async (ticketId: string) => {
    setLoading(true);
    try {
      const res = await fetchWithActivity(`${API_BASE_URL}/api/support-tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedTicket(data);
      }
    } catch (err) {
      console.error('Failed to fetch ticket detail:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (view === 'list') fetchTickets();
  }, [view, fetchTickets]);

  const handleCreateTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!subject.trim()) { setFormError('Subject is required'); return; }
    if (!description.trim()) { setFormError('Description is required'); return; }
    if (description.trim().length < 20) { setFormError('Description must be at least 20 characters'); return; }

    setSubmitting(true);
    try {
      const res = await fetchWithActivity(`${API_BASE_URL}/api/support-tickets`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject: subject.trim(), category, priority, description: description.trim() }),
      });
      if (res.ok) {
        const ticket = await res.json();
        setSuccessMessage(`Ticket ${ticket.ticketNumber} created successfully!`);
        setSubject('');
        setCategory('general');
        setPriority('medium');
        setDescription('');
        // Navigate to detail view
        setSelectedTicket(ticket);
        await fetchTicketDetail(ticket.id);
        setView('detail');
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        const err = await res.json();
        setFormError(err.error || 'Failed to create ticket');
      }
    } catch (err) {
      setFormError('Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async () => {
    if (!replyMessage.trim() || !selectedTicket) return;
    setReplySubmitting(true);
    try {
      const res = await fetchWithActivity(`${API_BASE_URL}/api/support-tickets/${selectedTicket.id}/replies`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: replyMessage.trim() }),
      });
      if (res.ok) {
        setReplyMessage('');
        await fetchTicketDetail(selectedTicket.id);
      }
    } catch (err) {
      console.error('Failed to send reply:', err);
    } finally {
      setReplySubmitting(false);
    }
  };

  const openTicket = async (ticket: SupportTicket) => {
    setLoading(true);
    setView('detail');
    await fetchTicketDetail(ticket.id);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  };

  const totalPages = Math.ceil(total / 20);

  return (
    <div className="px-4 sm:px-10 lg:px-40 flex flex-1 justify-center py-5 dark:bg-dark-bg transition-colors duration-200">
      <div className="layout-content-container flex flex-col max-w-[960px] flex-1">

        {/* Success banner */}
        {successMessage && (
          <div className="mx-4 mb-4 p-3 bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700 rounded-lg text-green-800 dark:text-green-300 text-sm">
            {successMessage}
          </div>
        )}

        {/* ========== LIST VIEW ========== */}
        {view === 'list' && (
          <>
            <div className="flex flex-wrap justify-between items-center gap-3 p-4">
              <p className="text-secondary dark:text-dark-text tracking-light text-[32px] font-bold leading-tight min-w-72">
                Support
              </p>
              <button
                onClick={() => setView('create')}
                className="bg-[#1993e5] hover:bg-blue-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors"
              >
                New Ticket
              </button>
            </div>

            <AppOverview />

            <UserGuide />

            {/* Filters */}
            <div className="px-4 pb-4 flex items-center gap-3">
              <label className="text-sm text-accent dark:text-dark-muted">Filter:</label>
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                className="text-sm border border-[#d0dee7] dark:border-dark-border rounded-lg px-3 py-1.5 bg-white dark:bg-dark-surface text-secondary dark:text-dark-text"
              >
                <option value="all">All Tickets</option>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="waiting_on_customer">Waiting on You</option>
                <option value="waiting_on_support">Waiting on Support</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
              <span className="text-sm text-accent dark:text-dark-muted ml-auto">{total} ticket{total !== 1 ? 's' : ''}</span>
            </div>

            {loading ? (
              <div className="p-8 text-center text-accent dark:text-dark-muted">Loading tickets...</div>
            ) : tickets.length === 0 ? (
              <div className="px-4 py-12 text-center">
                <div className="text-accent dark:text-dark-muted text-lg mb-2">No tickets found</div>
                <p className="text-accent dark:text-dark-muted text-sm mb-4">
                  {statusFilter !== 'all' ? 'Try changing your filter.' : 'Submit your first support ticket to get started.'}
                </p>
                {statusFilter === 'all' && (
                  <button
                    onClick={() => setView('create')}
                    className="text-primary hover:text-blue-600 text-sm font-medium"
                  >
                    Create a ticket →
                  </button>
                )}
              </div>
            ) : (
              <div className="px-4 space-y-3">
                {tickets.map(ticket => (
                  <div
                    key={ticket.id}
                    onClick={() => openTicket(ticket)}
                    className="bg-white dark:bg-dark-surface border border-[#d0dee7] dark:border-dark-border rounded-lg p-4 hover:border-[#1993e5] dark:hover:border-blue-500 cursor-pointer transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs text-accent dark:text-dark-muted font-mono">{ticket.ticketNumber}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[ticket.status] || ''}`}>
                            {STATUS_LABELS[ticket.status] || ticket.status}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PRIORITY_STYLES[ticket.priority] || ''}`}>
                            {ticket.priority}
                          </span>
                        </div>
                        <h3 className="text-secondary dark:text-dark-text font-medium truncate">{ticket.subject}</h3>
                      </div>
                      <span className="text-xs text-accent dark:text-dark-muted whitespace-nowrap">
                        {formatDate(ticket.createdAt)}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-accent dark:text-dark-muted">
                      <span className="capitalize">{ticket.category.replace('_', ' ')}</span>
                      {ticket.replies && ticket.replies.length > 0 && (
                        <span>{ticket.replies.length} {ticket.replies.length === 1 ? 'reply' : 'replies'}</span>
                      )}
                    </div>
                  </div>
                ))}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center gap-2 py-4">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="px-3 py-1 text-sm rounded border border-[#d0dee7] dark:border-dark-border text-secondary dark:text-dark-text disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-accent dark:text-dark-muted">
                      Page {page} of {totalPages}
                    </span>
                    <button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="px-3 py-1 text-sm rounded border border-[#d0dee7] dark:border-dark-border text-secondary dark:text-dark-text disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* ========== CREATE VIEW ========== */}
        {view === 'create' && (
          <>
            <div className="flex items-center gap-3 p-4">
              <button
                onClick={() => { setView('list'); setFormError(''); }}
                className="text-accent dark:text-dark-muted hover:text-secondary dark:hover:text-dark-text"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
              </button>
              <p className="text-secondary dark:text-dark-text tracking-light text-[32px] font-bold leading-tight">
                New Ticket
              </p>
            </div>

            <form onSubmit={handleCreateTicket} className="px-4 space-y-5 max-w-[640px]">
              {formError && (
                <div className="p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg text-red-800 dark:text-red-300 text-sm">
                  {formError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-secondary dark:text-dark-text mb-1.5">Subject *</label>
                <input
                  type="text"
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  placeholder="Brief summary of your issue"
                  maxLength={255}
                  className="w-full border border-[#d0dee7] dark:border-dark-border rounded-lg px-3 py-2 text-sm bg-white dark:bg-dark-surface text-secondary dark:text-dark-text placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1993e5] focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary dark:text-dark-text mb-1.5">Category</label>
                  <select
                    value={category}
                    onChange={e => setCategory(e.target.value)}
                    className="w-full border border-[#d0dee7] dark:border-dark-border rounded-lg px-3 py-2 text-sm bg-white dark:bg-dark-surface text-secondary dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-[#1993e5] focus:border-transparent"
                  >
                    {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary dark:text-dark-text mb-1.5">Priority</label>
                  <select
                    value={priority}
                    onChange={e => setPriority(e.target.value)}
                    className="w-full border border-[#d0dee7] dark:border-dark-border rounded-lg px-3 py-2 text-sm bg-white dark:bg-dark-surface text-secondary dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-[#1993e5] focus:border-transparent"
                  >
                    {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary dark:text-dark-text mb-1.5">Description *</label>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Describe your issue in detail (minimum 20 characters)"
                  rows={6}
                  className="w-full border border-[#d0dee7] dark:border-dark-border rounded-lg px-3 py-2 text-sm bg-white dark:bg-dark-surface text-secondary dark:text-dark-text placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1993e5] focus:border-transparent resize-y"
                />
                <p className="text-xs text-accent dark:text-dark-muted mt-1">{description.length} characters</p>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="bg-[#1993e5] hover:bg-blue-600 text-white px-6 py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {submitting ? 'Submitting...' : 'Submit Ticket'}
                </button>
                <button
                  type="button"
                  onClick={() => { setView('list'); setFormError(''); }}
                  className="border border-[#d0dee7] dark:border-dark-border text-secondary dark:text-dark-text px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-dark-surface transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </>
        )}

        {/* ========== DETAIL VIEW ========== */}
        {view === 'detail' && selectedTicket && (
          <>
            <div className="flex items-center gap-3 p-4">
              <button
                onClick={() => { setView('list'); setSelectedTicket(null); setReplyMessage(''); }}
                className="text-accent dark:text-dark-muted hover:text-secondary dark:hover:text-dark-text"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
              </button>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm text-accent dark:text-dark-muted font-mono">{selectedTicket.ticketNumber}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[selectedTicket.status] || ''}`}>
                    {STATUS_LABELS[selectedTicket.status] || selectedTicket.status}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PRIORITY_STYLES[selectedTicket.priority] || ''}`}>
                    {selectedTicket.priority}
                  </span>
                </div>
                <p className="text-secondary dark:text-dark-text text-xl font-bold leading-tight">
                  {selectedTicket.subject}
                </p>
              </div>
            </div>

            {/* Ticket metadata */}
            <div className="px-4 pb-4">
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-accent dark:text-dark-muted">
                <span>Created: {formatDate(selectedTicket.createdAt)}</span>
                <span className="capitalize">Category: {selectedTicket.category.replace('_', ' ')}</span>
                {selectedTicket.assignee && (
                  <span>Assigned to: {selectedTicket.assignee.firstName} {selectedTicket.assignee.lastName}</span>
                )}
              </div>
            </div>

            {/* Original description */}
            <div className="px-4 mb-4">
              <div className="bg-white dark:bg-dark-surface border border-[#d0dee7] dark:border-dark-border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-medium text-secondary dark:text-dark-text">
                    {selectedTicket.user ? `${selectedTicket.user.firstName} ${selectedTicket.user.lastName}` : 'You'}
                  </span>
                  <span className="text-xs text-accent dark:text-dark-muted">{formatDate(selectedTicket.createdAt)}</span>
                </div>
                <p className="text-sm text-secondary dark:text-dark-text whitespace-pre-wrap">{selectedTicket.description}</p>
              </div>
            </div>

            {/* Replies */}
            {selectedTicket.replies && selectedTicket.replies.length > 0 && (
              <div className="px-4 space-y-3 mb-4">
                {selectedTicket.replies.map(reply => (
                  <div
                    key={reply.id}
                    className={`border rounded-lg p-4 ${
                      reply.isStaffReply
                        ? 'bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800/30'
                        : 'bg-white dark:bg-dark-surface border-[#d0dee7] dark:border-dark-border'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium text-secondary dark:text-dark-text">
                        {reply.isStaffReply ? 'Risk Ninja Support' : `${reply.user.firstName} ${reply.user.lastName}`}
                      </span>
                      {reply.isStaffReply && (
                        <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded font-medium">Staff</span>
                      )}
                      <span className="text-xs text-accent dark:text-dark-muted">{formatDate(reply.createdAt)}</span>
                    </div>
                    <p className="text-sm text-secondary dark:text-dark-text whitespace-pre-wrap">{reply.message}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Reply form */}
            {selectedTicket.status !== 'closed' ? (
              <div className="px-4 pb-4">
                <div className="bg-white dark:bg-dark-surface border border-[#d0dee7] dark:border-dark-border rounded-lg p-4">
                  <textarea
                    value={replyMessage}
                    onChange={e => setReplyMessage(e.target.value)}
                    placeholder="Write your reply..."
                    rows={3}
                    className="w-full border border-[#d0dee7] dark:border-dark-border rounded-lg px-3 py-2 text-sm bg-white dark:bg-dark-surface text-secondary dark:text-dark-text placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1993e5] focus:border-transparent resize-y mb-3"
                  />
                  <button
                    onClick={handleReply}
                    disabled={!replyMessage.trim() || replySubmitting}
                    className="bg-[#1993e5] hover:bg-blue-600 text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                  >
                    {replySubmitting ? 'Sending...' : 'Send Reply'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="px-4 pb-4">
                <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-lg text-center text-sm text-accent dark:text-dark-muted">
                  This ticket is closed and no longer accepting replies.
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Support;
